<template>
  <v-container
    v-if="order && order.order_pickup"
  >
    <v-breadcrumbs
      class="px-0 py-2"
      :items="breadcrumbs"
    />
    <h2>
      積替運搬からの受注登録
    </h2>
    <validation-observer ref="observer">
      <v-card
        class="rounded-card"
        flat
      >
        <v-card-text>
          <v-row
            v-if="isBilling"
            justify="end"
          >
            <v-col cols="6">
              <v-checkbox
                v-model="order.not_recorded_sales"
                dense
                label="売上計上しない"
                hide-details="auto"
              >
              </v-checkbox>
            </v-col>
          </v-row>
          <!-- 引取搬入番号/営業担当者 -->
          <v-row>
            <v-col cols="12" md="6">
              <div class="overline">
                引取搬入番号
              </div>
              <span>{{ order.request_no }}&nbsp;</span>
              <v-divider />
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                name="営業担当者"
                rules="max:12"
              >
                <v-combobox
                  v-model="order.sales_staff"
                  :error-messages="errors"
                  :items="salesStaffs"
                  :return-object="false"
                  dense
                  label="営業担当者"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>営業担当者</span>
                    <span class="ml-1">
                      ({{ order.sales_staff?.length || 0 }} / 12)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /引取搬入番号/営業担当者 -->

          <!-- 依頼元業者 -->
          <v-row v-if="order.request_trader">
            <v-col cols="12" md="4">
              <h4 class="field-label">
                依頼元業者
              </h4>
              <ul style="list-style: none">
                <li>{{ order.request_trader.name }}</li>
                <li v-if="order.request_trader.address">
                  住所: <span v-if="order.request_trader.zip_code">〒{{ order.request_trader.zip_code }}&nbsp;</span>
                  {{ order.request_trader.address }}
                </li>
                <li v-if="order.request_trader.phone_number">
                  電話番号: {{ order.request_trader.phone_number }}
                </li>
              </ul>
            </v-col>
          </v-row>
          <!-- /依頼元業者 -->

          <!-- 引取場所 -->
          <v-row v-if="order.trader_location">
            <v-col>
              <h4 class="field-label">
                引取場所
              </h4>
              <ul style="list-style: none">
                <li>
                  {{ order.trader_location.name }}
                </li>
                <li v-if="order.trader_location.address">
                  住所:<span v-if="order.trader_location.zip_code">〒{{ order.trader_location.zip_code }}&nbsp;</span>
                  {{ order.trader_location.address }}
                </li>
                <li v-if="order.trader_location.phone_number">
                  電話番号: {{ order.trader_location.phone_number }}
                </li>
              </ul>
            </v-col>
          </v-row>
          <!-- /引取場所 -->

          <!-- 運搬先 -->
          <v-row v-if="order.trader">
            <v-col cols="12" md="6">
              <h4 class="field-label">運搬先</h4>
              <ul style="list-style: none">
                <li>
                  {{ order.trader.name }}
                </li>
                <li v-if="order.trader.address">
                  住所:<span v-if="order.trader.zip_code">〒{{ order.trader.zip_code }}&nbsp;</span>
                  {{ order.trader.address }}
                </li>
                <li v-if="order.trader.phone_number">
                  電話番号: {{ order.trader.phone_number }}
                </li>
              </ul>
            </v-col>
            <v-col cols="12" md="6">
              <h4>運搬先事業場</h4>
              <div class="ml-3 mt-1">
                <h4>{{ order.trader.location.name }}</h4>
                <p class="mb-0">
                  <span v-if="order.trader.location.zip_code">〒{{ order.trader.location.zip_code }}&nbsp;</span>
                  {{ order.trader.location.address }}
                </p>
                <p>電話番号:{{ order.trader.location.phone_number || '-' }}</p>
              </div>
            </v-col>
          </v-row>
          <!-- /運搬先 -->

          <v-row>
            <v-col cols="12" md="3">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                rules="required"
                name="積替運搬日"
              >
                <vue-ctk-date-time-picker
                  v-model="order.plan_date"
                  no-value-to-custom-elem
                  only-date
                  no-button
                  color="#0070B3"
                  format="YYYY-MM-DD"
                  formatted="YYYY-MM-DD"
                  no-header
                  auto-close
                >
                  <v-text-field
                    v-model="order.plan_date"
                    :error-messages="errors"
                    append-icon="mdi-calendar"
                    clearable
                    dense
                    hide-details="auto"
                    label="積替運搬日"
                    outlined
                    persistent-hint
                    persistent-placeholder
                    readonly
                  />
                </vue-ctk-date-time-picker>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="3">
              <validation-provider
                v-slot="{ errors }"
                :rules="{preferredStartTimeCheck: {preferred_end_time: order.plan_end_time}}"
                name="希望時間"
              >
                <vue-ctk-date-time-picker
                  v-model="order.plan_start_time"
                  minute-interval="15"
                  no-value-to-custom-elem
                  only-time
                  color="#0070B3"
                  format="HH:mm"
                  formatted="HH:mm"
                >
                  <v-text-field
                    v-model="order.plan_start_time"
                    :error-messages="errors"
                    append-icon="mdi-clock-time-four-outline"
                    clearable
                    dense
                    hide-details="auto"
                    label="希望時間"
                    outlined
                    persistent-hint
                    persistent-placeholder
                    placeholder="--:--"
                    readonly
                  />
                </vue-ctk-date-time-picker>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="3">
              <vue-ctk-date-time-picker
                v-model="order.plan_end_time"
                minute-interval="15"
                color="#0070B3"
                format="HH:mm"
                formatted="HH:mm"
                only-time
                no-value-to-custom-elem
              >
                <v-text-field
                  v-model="order.plan_end_time"
                  append-icon="mdi-clock-time-four-outline"
                  clearable
                  outlined
                  label="終了時間"
                  dense
                  persistent-placeholder
                  hide-details="auto"
                  placeholder="--:--"
                  readonly
                />
              </vue-ctk-date-time-picker>
            </v-col>
          </v-row>

          <!-- 運搬担当者／運搬方法／車両番号 -->
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :vid="`transport_user_name`"
                :rules="{ max: 12 }"
                name="運搬担当者"
              >
                <v-combobox
                  v-model="order.transport_user_name"
                  :error-messages="errors"
                  :items="drivers"
                  :return-object="false"
                  clearable
                  dense
                  hide-details="auto"
                  item-value="text"
                  label="運搬担当者"
                  outlined
                  persistent-placeholder
                  @click:clear="transportUserClearClickEvent"
                  @change="transportUserChangeEvent"
                >
                  <template v-slot:label>
                    <span>運搬担当者</span>
                    <span class="ml-1">
                      ({{ order.transport_user_name?.length || 0 }} / 12)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>

            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :vid="`transportation_id`"
                name="運搬方法"
              >
                <v-select
                  v-model="order.transportation_id"
                  :error-messages="errors"
                  :items="transportations"
                  dense
                  clearable
                  hide-details="auto"
                  label="運搬方法"
                  outlined
                  persistent-placeholder
                ></v-select>
              </validation-provider>
            </v-col>

            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :vid="`vehicle_number`"
                name="車両番号"
                rules="max:15"
              >
                <v-combobox
                  v-model="order.vehicle_number"
                  :error-messages="errors"
                  :items="vehicles"
                  :return-object="false"
                  :clearable="true"
                  item-text="number"
                  item-value="number"
                  dense
                  label="車両番号"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>車両番号</span>
                    <span class="ml-1">
                      ({{ order.vehicle_number?.length || 0 }} / 15)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /運搬担当者／運搬方法／車両番号 -->


          <!-- ドライバー指示メモ -->
          <v-row>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                name="ドライバー指示メモ"
                rules="max:300"
              >
                <v-textarea
                  v-model="order.driver_instruction"
                  :error-messages="errors"
                  hide-details="auto"
                  label="ドライバー指示メモ"
                  no-resize
                  outlined
                  persistent-placeholder
                  required
                >
                  <template v-slot:label>
                    <span>ドライバー指示メモ</span>
                    <span class="ml-1">
                      ({{ order.driver_instruction?.length || 0 }} / 300)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-textarea>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /ドライバー指示メモ -->
          <!-- 添付ファイル -->
          <v-row>
            <v-col cols="12">
              <dropzone-file
                :max-size="MANIFEST_ATTACHMENT.MAX_SIZE"
                :accepts="MANIFEST_ATTACHMENT.ACCEPTS"
                :max-file-size="MANIFEST_ATTACHMENT.MAX_FILE_SIZE"
                :alt-file-size-format="MANIFEST_ATTACHMENT.ALT_FILE_SIZE_FORMAT"
                :alt-file-format="MANIFEST_ATTACHMENT.ALT_FILE_FORMAT"
                :immediate-delete="MANIFEST_ATTACHMENT.IMMEDIATE_DELETE"
                :files.sync="files"
              />
            </v-col>
          </v-row>
          <!-- 検索キーワード -->
          <v-row>
            <v-col cols="12" md="12">
              <validation-provider
                v-slot="{ errors }"
                name="検索キーワード"
                rules="max:25"
              >
                <v-text-field
                  v-model="order.keyword"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="検索キーワード"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>検索キーワード</span>
                    <span class="ml-1">
                      ({{ order.keyword?.length || 0 }} / 25)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /検索キーワード -->
        </v-card-text>

        <v-card-text>
          <v-row>
            <v-col
              v-if="order.is_items"
              cols="12"
              md="5"
            >
              <v-card-title class="text-h6">
                マニフェスト情報
              </v-card-title>
              <!-- マニフェスト情報テーブル -->
              <v-data-table
                :v-if="order.is_items"
                :headers="manifestTableHeaders"
                :items="order.order_manifests"
                :items-per-page="-1"
                item-key="key"
                dense
                disable-sort
                hide-default-footer
                style="max-height: 50vh;overflow-y: auto;"
              >
                <template v-slot:[`item.manifest_total_weight`]="{ item }">
                  <span v-if="item.manifest_total_weight && item.manifest_total_weight_unit_id">{{ parseFloat(item.manifest_total_weight) | comma_format }}&nbsp;{{ item.manifest_total_weight_unit_name }}</span>
                  <span v-else>ー</span>
                </template>
              </v-data-table>
              <hr width=”50%″>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                  <tr>
                    <th width="30">合計数量</th>
                    <td class="text-right" width="35%">
                      <span>{{ manifestTotalCount | comma_format }}</span>
                    </td>
                    <td class="text-right" width="35%">
                      <span v-if="manifestTotalWeight > 0"> {{ manifestTotalWeight | comma_format }}&nbsp;{{ manifestUnitName }}</span>
                      <span v-else>ー</span>
                    </td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <!-- /マニフェスト情報テーブル -->
            </v-col>
            <v-col
              v-if="isBilling && !order.not_recorded_sales"
              cols="12"
              :md="order.is_items ? 7 : 12"
            >
              <v-card-title class="text-h6">
                <validation-provider
                  v-slot="{ errors }"
                  class="requiredMark"
                  name="受注情報"
                  rules="required"
                >
                  <v-input
                    :value="filteredSalesItems.length > 0 ? 1 : null"
                    :error-messages="errors"
                    dense
                    label="受注情報"
                    hide-details="auto"
                    outlined
                  />
                </validation-provider>
                <v-spacer />
                <v-btn
                  class="primary"
                  @click="addSalesItemButtonClickEvent"
                >
                  追加
                </v-btn>
              </v-card-title>
              <!-- 受注情報テーブル -->
              <v-data-table
                :headers="salesTableHeaders"
                :items="filteredSalesItems"
                :items-per-page="-1"
                item-key="key"
                dense
                disable-sort
                hide-default-footer
                style="max-height: 50vh;overflow-y: auto;"
              >
                <!-- 売上品目コード -->
                <template v-slot:[`item.sales_item_code`]="{item}">
                  <span v-if="item.amount_of_acceptance && item.amount_of_acceptance_unit_name">{{ item.sales_item_code }}</span>
                </template>
                <!-- /売上品目コード -->
                <!-- 受入量 -->
                <template v-slot:[`item.amount_of_acceptance`]="{item}">
                  <span v-if="item.amount_of_acceptance && item.amount_of_acceptance_unit_name">{{ parseFloat(item.amount_of_acceptance) | comma_format }}&nbsp;{{ item.amount_of_acceptance_unit_name }}</span>
                </template>
                <!-- /受入量 -->
                <template v-slot:[`item.editable`]="{ item }">
                  <v-btn
                    small
                    class="info"
                    @click="editSalesItemClickEvent(item)"
                  >
                    編集
                  </v-btn>
                  &nbsp;
                  <v-btn
                    small
                    class="error"
                    @click="deleteSalesItemClickEvent(item)"
                  >
                    削除
                  </v-btn>
                </template>
              </v-data-table>
              <hr>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                  <tr>
                    <th class="text-right" width="22.2%">受入量合計</th>
                    <td class="text-right" width="22.2%"></td>
                    <td class="text-right" width="22.2%">
                      <span v-if="totalAmountOfAcceptance > 0">{{ totalAmountOfAcceptance | comma_format }}&nbsp;{{ acceptanceUnitName }}</span>
                      <span v-else>{{ "ー" }}</span>
                    </td>
                    <td width="33.4%"></td>
                  </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <!-- /受注情報テーブル -->
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <transition-manifest-button
            v-if="order.order_manifests.length > 0"
            :request-no="order.request_no"
            :order-type="'pickup_r'"
            :order-id="order.id"
          />
          <v-spacer />
          <v-btn
            @click="cancelButtonClickEvent"
          >
            閉じる
          </v-btn>
          <v-btn
            :disabled="order === null"
            class="warning"
            @click="rejectionButtonClickEvent"
          >
            否決通知
          </v-btn>
          <v-btn
            :disabled="order === null"
            class="primary"
            @click="receptionButtonClickEvent"
          >
            受付通知
          </v-btn>
        </v-card-actions>
      </v-card>
    </validation-observer>
    <sales-item-dialog
      :enable-dialog="enableSalesItemDialog"
      :sales-item="salesItem"
      :sales-item-prices="salesItemPrices"
      :sales-item-units="salesItemUnits"
      @cancelCallback="dialogCancelCallback"
      @registerCallback="dialogRegisterCallback"
    />
    <confirm ref="confirm" />
    <rejection-reason-dialog
      :enable-dialog="enableRejectionReasonDialog"
      @closeCallback="rejectionReasonDialogCloseCallback"
      @registerCallback="rejectionReasonDialogRegisterCallback"
    />
    <registration-provisional-dialog
      :enable-dialog="enableRegistrationProvisionalDialog"
      :enable-to-deal-button="!order.not_recorded_sales"
      :fromCollectionRequest="false"
      :repeatSetting="'unuse_repeat'"
      @closeCallback="registrationProvisionalDialogCloseCallback"
      @registerCallback="registrationProvisionalRegisterCallback"
    />
  </v-container>
</template>
<script>
import _ from 'lodash'
import RejectionReasonDialog from '../../../components/_RejectionReasonDialog'
import SalesItemDialog from '../../../components/_SalesItemDialog'
import RegistrationProvisionalDialog from '../../dialogs/_RegistrationProvisional'
import DropzoneFile from '../../../../../../components/DropzoneFile'
import { MANIFEST_ATTACHMENT } from '@/constants/appConst'
import Confirm from '@/components/Confirm'
import {mapActions, mapState} from 'vuex'
import TransitionManifestButton from '../../dialogs/_TransitionManifestButton'

export default {
  name: 'DriverOrderAcceptPickupRegistrationEdit',
  components: {
    TransitionManifestButton,
    RejectionReasonDialog,
    SalesItemDialog,
    RegistrationProvisionalDialog,
    DropzoneFile,
    Confirm,
  },
  mixins: [
  ],
  props: {},
  data() {
    return {
      enableRejectionReasonDialog: false,
      enableSalesItemDialog: false,
      enableRegistrationProvisionalDialog: false,
      breadcrumbs: [],
      order: {},
      files: [],
      manifestTotalCount: 0,
      manifestTotalWeight: 0,
      totalAmountOfAcceptance: 0,
      /** マニフェスト情報 */
      manifestTableHeaders: [
        { value: 'item_name', text: 'JWNET品目', cellClass: 'tableDisabledField', class: 'tableDisabledField' , width: '30%'},
        { value: 'manifest_total_count', text: 'マニフェスト数', align: 'end', cellClass: 'tableDisabledField', class: 'tableDisabledField' , width: '35%'},
        { value: 'manifest_total_weight', text: 'マニフェスト数量', align: 'end', cellClass: 'tableDisabledField', class: 'tableDisabledField', width: '35%'},
      ],
      /** 受注情報 */
      salesTableHeaders: [
        { value: 'sales_item_code', text: '売上品目コード', align: 'end' , width: '22.2%'},
        { value: 'sales_item_name', text: '売上品目名' , width: '22.2%'},
        { value: 'amount_of_acceptance', text: '受入量', align: 'end' , width: '22.2%'},
        { value: 'editable', text: '', align: 'end' , width: '33.4%'},
      ],
      salesItem: null,
      salesStaffs:[],
      salesItemPrices: [],
      salesItemUnits: [],
      drivers: [],
      transportations: [],
      vehicles: [],
    }
  },
  computed: {
    ...mapState('DriverTenantConfiguration', {
      isBilling: 'use_billing',
    }),
    MANIFEST_ATTACHMENT() {
      return MANIFEST_ATTACHMENT
    },
    filteredSalesItems () {
      let filteredList = []
      if (this.order.order_pickup_details) {
        filteredList = this.order.order_pickup_details.filter(v => !v._destroy)
      }
      return filteredList
    },
  },
  watch: {
    'order.order_pickup_details': function () {
      this.calculateTotalAmount()
    },
  },
  created() {
    this.breadcrumbs = []
    if (this.$route.query.d !== undefined) {
      this.breadcrumbs.push({ text: 'ダッシュボード', disabled: false, to: '/driver/home', exact: true })
    } else {
      this.breadcrumbs.push({ text: '受注一覧', disabled: false, to: '/driver/orders/accepts', exact: true })
    }
    this.breadcrumbs.push({ text: '積替運搬からの受注登録', disabled: true, to: '' })

    this.fetchPickupOrder({id: this.$route.params.id}).then((content) => {
      this.order = content
      this.fetchSalesItemPrices({id: this.order.request_trader.id, type: 'Trader', only_jwnet_item: true}).then((content) => {
        this.salesItemPrices = content.sales_item_price_details
        this.calculateTotalAmount()
      })
    })
    this.fetchSalesStaffs().then((content) => {
      this.salesStaffs = content.sales_staffs
    })
    this.fetchSalesItemUnits().then((content) => {
      this.salesItemUnits = content.sales_item_units
    })
    this.fetchDrivers({without_invisible: true}).then((content) => { this.drivers = content.drivers })
    this.fetchTransportations().then((content) => {
      this.transportations = content.transportations
    })
    this.fetchVehicles({ registered_number: true}).then((content) => { this.vehicles = content.vehicles })
  },
  methods: {
    ...mapActions('DriverOrderAcceptPickupRegistration', {
      fetchPickupOrder: 'edit',
    }),
    ...mapActions('DriverOrderAcceptPickupRegistrationReception', {
      receptionPickup: 'update',
    }),
    ...mapActions('DriverOrderAcceptPickupRegistrationRejection', {
      rejectionPickup: 'update',
    }),
    ...mapActions('DriverMasterSearchSupplierSalesItemPriceDetail', {
      fetchSalesItemPrices: 'index'
    }),
    ...mapActions('DriverMasterSearchSalesItemUnit', {
      fetchSalesItemUnits: 'index'
    }),
    ...mapActions('DriverMasterSearchSalesStaff', {
      fetchSalesStaffs: 'index',
    }),
    ...mapActions('DriverMasterSearchDriver', {
      fetchDrivers: 'index',
    }),
    ...mapActions('DriverMasterSearchTransportation', {
      fetchTransportations: 'index'
    }),
    ...mapActions('DriverMasterSearchVehicle', {
      fetchVehicles: 'search',
    }),
    /**
     * キャンセルボタン クリックイベント
     */
    cancelButtonClickEvent () {
      this.previousControl()
    },
    /**
     * 対象マニフェストへボタン クリックイベント
     */
    manifestButtonClickEvent() {
      this.$router.push( { name: 'DriverManifestManagements' } )
    },
    /**
     * 運搬担当者 チェンジイベント
     */
    transportUserChangeEvent (item) {
      const transportUser = this.drivers.find( (v) => v.text.replace(/\s+/g,'') === item.replace(/\s+/g,''))
      if (transportUser) {
        this.order.transport_user_id = transportUser.value
      } else {
        this.order.transport_user_id = null
      }
      console.log("transport_user_id", this.order.transport_user_id)
      console.log("transport_user_name", this.order.transport_user_name)
    },
    /**
     * 運搬担当者クリア クリックイベント
     */
    transportUserClearClickEvent () {
      this.order.transport_user_id = null
      this.order.transport_user_name = null
      console.log("transport_user_id", this.order.transport_user_id)
      console.log("transport_user_name", this.order.transport_user_name)
    },
    /**
     * 否決通知ボタン クリックイベント
     */
    rejectionButtonClickEvent () {
      this.enableRejectionReasonDialog = true
    },
    /**
     * 受付通知ボタン クリックイベント
     */
    receptionButtonClickEvent () {
      this.$refs.observer.validateWithInfo().then(({ isValid, errors }) => {
        if (isValid) {
          this.enableRegistrationProvisionalDialog = true
        }
      })
    },
    /**
     * 受付通知登録ダイアログ キャンセルボタンコールバック
     */
    registrationProvisionalDialogCloseCallback () {
      this.enableRegistrationProvisionalDialog = false
    },
    /**
     * 受付通知登録ダイアログ 登録ボタンコールバック
     * @param afterTransaction
     */
    registrationProvisionalRegisterCallback (afterTransaction) {
      this.enableRegistrationProvisionalDialog = false
      const params = this.buildParams()
      this.receptionPickup({ id: this.$route.params.id, params: params }).then((content) => {
        const deal = content.deal
        if (afterTransaction) { // OKして売上入力へ進む
          this.$router.replace({name: 'DriverSalesTransactionEdit', params: { id: deal.id }}, () => {})
        } else { // OKして閉じる(一覧画面に戻る)
          this.$router.replace({name: 'DriverOrderAccepts'}, () => {})
        }
      })
    },
    addSalesItemButtonClickEvent () {
      this.salesItem = null
      this.enableSalesItemDialog = true
    },
    editSalesItemClickEvent (item) {
      this.salesItem = _.cloneDeep(item)
      this.enableSalesItemDialog = true
    },
    deleteSalesItemClickEvent (item) {
      let modalOption = { title: '削除', message: '削除しますがよろしいですか？', options: { width: 320, titleColor: 'red', isCancelButton: true } }
      this.$refs.confirm.open(modalOption).then((confirm) => {
        this.$refs.confirm.close()
        if (confirm) {
          for (let i = 0; i < this.order.order_pickup_details.length; i++) {
            if (item.key === this.order.order_pickup_details[i].key) {
              this.order.order_pickup_details[i]._destroy = true
              break
            }
          }
        }
      })
    },
    dialogCancelCallback () {
      this.enableSalesItemDialog = false
    },
    dialogRegisterCallback (item) {
      let detail = this.order.order_pickup_details.find(v => v.key === item.key)
      if (detail) {
        Object.assign(detail, item)
      } else {
        this.order.order_pickup_details.push(item)
      }
      this.calculateTotalAmount()
      this.dialogCancelCallback()
    },
    /**
     * 再計算処理
     * すべての受入量の単位が同じ場合、合算する。それ以外は0にして画面で「-」を表示
     */
    calculateTotalAmount() {
      this.manifestTotalCount = 0
      this.manifestTotalWeight = 0
      this.totalAmountOfAcceptance = 0
      if (this.order.is_items) {

        let manifestUnitAllEqual = this.order.order_manifests.every(v => v.manifest_total_weight_unit_name === this.order.order_manifests[0].manifest_total_weight_unit_name)
        if (manifestUnitAllEqual) {

          // マニフェスト数量合計 単位
          this.manifestUnitName = this.order.order_manifests[0].manifest_total_weight_unit_name
          // マニフェスト数量合計
          for (const manifest_item of this.order.order_manifests) {
            this.manifestTotalCount += manifest_item.manifest_total_count
            this.manifestTotalWeight += parseFloat(manifest_item.manifest_total_weight)
          }
        }
      }

      // 受入量単位あり かつ 表示中のリスト
      let isAcceptanceUnitList = this.order.order_pickup_details.filter(v => v.amount_of_acceptance_unit_name !== null && !v._destroy)
      if (isAcceptanceUnitList.length > 0) {

        let acceptanceUnitAllEqual = isAcceptanceUnitList.every(v => v.amount_of_acceptance_unit_name === isAcceptanceUnitList[0].amount_of_acceptance_unit_name)
        if (acceptanceUnitAllEqual) {
          // 受入量合計 単位
          this.acceptanceUnitName = isAcceptanceUnitList[0].amount_of_acceptance_unit_name
          // 受入量合計
          for (const sales_item of isAcceptanceUnitList) {
            this.totalAmountOfAcceptance += parseFloat(sales_item.amount_of_acceptance)
          }
        }
      }
    },
    /**
     * 否決理由登録ダイアログ 閉じるボタンコールバック
     */
    rejectionReasonDialogCloseCallback () {
      this.enableRejectionReasonDialog = false
    },
    /**
     * 否決理由登録ダイアログ 登録ボタンコールバック
     * @param forms
     */
    rejectionReasonDialogRegisterCallback (forms) {
      const params = { rejection_reason: forms.rejectionReason }
      this.rejectionPickup({ id: this.$route.params.id, params: params }).then(() => {
        this.rejectionReasonDialogCloseCallback()
        this.$router.push({ name: 'DriverOrderAccepts' })
      })
    },
    buildParams () {
      const params = new FormData ()
      /* 売上計上しない */
      params.append('not_recorded_sales', this.nullToBlank(this.order.not_recorded_sales))
      /* 営業担当者 */
      params.append('sales_staff', this.nullToBlank(this.order.sales_staff))
      /* ドライバー指示メモ */
      params.append('driver_instruction', this.nullToBlank(this.order.driver_instruction))
      /* 検索キーワード */
      params.append('keyword', this.nullToBlank(this.order.keyword))
      /* 予定日 */
      params.append('plan_date', this.nullToBlank(this.order.plan_date))
      /* 予定開始時間 */
      params.append('plan_start_time', this.nullToBlank(this.order.plan_start_time))
      /* 予定終了時間 */
      params.append('plan_end_time', this.nullToBlank(this.order.plan_end_time))
      /* 運搬担当者 */
      params.append('transport_user_id', this.nullToBlank(this.order.transport_user_id))
      params.append('transport_user_name', this.nullToBlank(this.order.transport_user_name))
      /* 運搬方法　*/
      params.append('transportation_id', this.nullToBlank(this.order.transportation_id))
      /* 運搬車両名 */
      params.append('vehicle_number', this.nullToBlank(this.order.vehicle_number))
      /* オーダー搬入ID */
      params.append('order_pickup_attributes[id]', this.nullToBlank(this.order.order_pickup.id))
      /* 積替運搬日 */
      params.append('order_pickup_attributes[request_date]', this.nullToBlank(this.order.plan_date))
      /* 希望開始時間 */
      params.append('order_pickup_attributes[preferred_start_time]', this.nullToBlank(this.order.plan_start_time))
      /* 希望終了時間 */
      params.append('order_pickup_attributes[preferred_end_time]', this.nullToBlank(this.order.plan_end_time))

      if (this.isBilling && !this.order.not_recorded_sales) {
        this.order.order_pickup_details.forEach((order_pickup_detail, index) => {
          /* ID */
          params.append(`order_pickup_attributes[order_pickup_details_attributes][${index}][id]`, this.nullToBlank(order_pickup_detail.id))
          /* 売上品目コード */
          params.append(`order_pickup_attributes[order_pickup_details_attributes][${index}][sales_item_price_code]`, this.nullToBlank(order_pickup_detail.sales_item_code))
          /* 売上品目名 */
          params.append(`order_pickup_attributes[order_pickup_details_attributes][${index}][sales_item_price_name]`, this.nullToBlank(order_pickup_detail.sales_item_name))
          /* 受入量 */
          params.append(`order_pickup_attributes[order_pickup_details_attributes][${index}][received_quantity]`, this.nullToBlank(order_pickup_detail.amount_of_acceptance))
          /* 受入量単位 */
          params.append(`order_pickup_attributes[order_pickup_details_attributes][${index}][sales_item_unit_id]`, this.nullToBlank(order_pickup_detail.amount_of_acceptance_unit_id))
        })
      }

      /* 添付ファイル */
      for (const file of this.files) {
        // 新規 or 削除対象のみ
        if (!file.file && !file._destroy) continue
        if (file.id) {
          params.append('order_attachments_attributes[][id]', file.id)
        }
        params.append('order_attachments_attributes[][file]', file.file)
        if (file._destroy) {
          params.append('order_attachments_attributes[][_destroy]', file._destroy)
        }
      }
      return params
    },
    /**
     * 戻る処理
     */
    previousControl () {
      if (this.$route.query.d !== undefined) {
        this.$router.push( { name: 'DriverDashboard' } )
      } else {
        this.$router.push( { name: 'DriverOrderAccepts' } )
      }
    },
  }
}
</script>
